import ky, { HTTPError } from 'ky';
import { env } from './env';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|; )forest\.csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
}

export const api = ky.create({
  prefixUrl: env.VITE_API_URL + '/api',
  credentials: 'include',
  timeout: 30_000,
  hooks: {
    beforeRequest: [
      (request) => {
        const method = request.method.toUpperCase();
        if (method !== 'GET' && method !== 'HEAD') {
          const csrf = getCsrfToken();
          if (csrf) request.headers.set('X-CSRF-Token', csrf);
        }
        const lang = useUiStore.getState().language;
        request.headers.set('Accept-Language', lang);
      },
    ],
    afterResponse: [
      async (_req, _opts, response) => {
        if (response.status === 401) {
          useAuthStore.getState().clear();
        }
        return response;
      },
    ],
  },
});

export type ApiErrorDetail = { path: string; code: string; message: string };

export interface ApiErrorInfo {
  status: number | null;
  code: string;
  message: string;
  details?: ApiErrorDetail[];
  entitiesOutOfScope?: string[];
}

export async function apiError(err: unknown): Promise<ApiErrorInfo> {
  if (err instanceof HTTPError) {
    try {
      const body = (await err.response.json()) as {
        error?: {
          code?: string;
          message?: string;
          details?: ApiErrorDetail[];
          entitiesOutOfScope?: string[];
        };
      };
      return {
        status: err.response.status,
        code: body.error?.code ?? 'INTERNAL',
        message: body.error?.message ?? err.message,
        ...(Array.isArray(body.error?.details) ? { details: body.error.details } : {}),
        ...(body.error?.entitiesOutOfScope
          ? { entitiesOutOfScope: body.error.entitiesOutOfScope }
          : {}),
      };
    } catch {
      return { status: err.response.status, code: 'INTERNAL', message: err.message };
    }
  }
  return { status: null, code: 'INTERNAL', message: (err as Error).message };
}
